import { useState, useEffect } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';

// User document interface based on design specification
export interface UserDocument {
  role?: 'free' | 'plus' | 'pro';
  prePremiumRole?: 'free' | 'plus' | 'pro';
  entitlements?: {
    pro?: {
      status: 'active' | 'cancelled' | 'expired';
      stripeCustomerId?: string;
      subscriptionId?: string;
      expiresAt?: Timestamp;
      productId?: string;
    };
  };
  ospAiProActiveUntil?: Timestamp;
  ospSubscriptionType?: 'trial' | 'full' | 'ultimate';
  ospSubscriptionStatus?: string;
  ospUsage?: {
    limit: number;
    hasUsedTrial?: boolean;
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Hook return interface
export interface EntitlementsResult {
  user: User | null;
  userDoc: UserDocument | null;
  hasPro: boolean;
  hasPlus: boolean;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

/**
 * Custom hook for managing user entitlements and Pro status
 * Combines Firebase Auth state with Firestore user document subscription
 * Provides real-time updates when user Pro status changes
 */
export function useEntitlements(): EntitlementsResult {
  const [user, setUser] = useState<User | null>(null);
  const [userDoc, setUserDoc] = useState<UserDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    // Subscribe to Firebase Auth state changes
    const unsubscribeAuth = onAuthStateChanged(auth, (authUser) => {
      setUser(authUser);
      setError(null); // Clear errors on auth state change

      // If user is signed out, reset everything
      if (!authUser) {
        setUserDoc(null);
        setLoading(false);
        return;
      }
    });

    return unsubscribeAuth;
  }, []);

  useEffect(() => {
    // Subscribe to Firestore user document when user is available
    if (!user) {
      return;
    }

    const userDocRef = doc(db, 'users', user.uid);

    const unsubscribeDoc = onSnapshot(
      userDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setUserDoc(docSnapshot.data() as UserDocument);
        } else {
          // User document doesn't exist yet, set to null
          setUserDoc(null);
        }
        setError(null); // Clear errors on successful data load
        setLoading(false);
      },
      (firestoreError) => {
        console.error('Error listening to user document:', firestoreError);

        // Set user-friendly error message based on error type
        if (firestoreError.code === 'permission-denied') {
          setError('Nemáš oprávnění k přístupu k datům účtu. Zkus se přihlásit znovu.');
        } else if (firestoreError.code === 'unavailable') {
          setError('Služba je dočasně nedostupná. Zkus to za chvilku.');
        } else {
          setError('Problém s načítáním dat účtu. Zkus obnovit stránku.');
        }

        setUserDoc(null);
        setLoading(false);
      }
    );

    return unsubscribeDoc;
  }, [user, retryCount]); // Include retryCount to trigger retry

  // Calculate Pro status based on requirements 6.4
  // hasPro is true if userDoc?.role === 'pro' OR userDoc?.entitlements?.pro?.status === 'active'
  // ALSO includes Premium/Ultimate subscribers and active OSP AI Pro users
  const isPastDueOrCanceled =
    userDoc?.ospSubscriptionStatus === 'past_due' ||
    userDoc?.ospSubscriptionStatus === 'unpaid' ||
    userDoc?.ospSubscriptionStatus === 'canceled';

  const hasPro = Boolean(
    userDoc?.role === 'pro' ||
    userDoc?.entitlements?.pro?.status === 'active' ||
    (!isPastDueOrCanceled && (
      userDoc?.ospSubscriptionType === 'ultimate' ||
      (userDoc?.ospAiProActiveUntil && userDoc.ospAiProActiveUntil.toMillis() > Date.now())
    ))
  );

  // Calculate Plus status
  // hasPlus is true if user has Pro OR userDoc?.role === 'plus'
  const hasPlus = Boolean(
    hasPro ||
    userDoc?.role === 'plus'
  );

  // Retry function to manually trigger data refresh
  const retry = () => {
    setError(null);
    setLoading(true);
    setRetryCount(prev => prev + 1);
  };

  return {
    user,
    userDoc,
    hasPro,
    hasPlus,
    loading,
    error,
    retry
  };
}